import './panel.tw.css';
import DesignerOptionsPanel from './DesignerOptionsPanel.vue';
import { apply, init, resetBaseline } from './designerController';
import { t } from './i18n';

type VeyopDesignerRuntime = {
  hooks?: {
    on?: (event: string, handler: (payload: unknown) => void) => void;
    whenReady?: (surface: string, handler: (payload: unknown) => void) => void;
  };
  designer?: {
    registerLeftPanel?: (panel: {
      id: string;
      label: string;
      description?: string;
      icon?: string;
      priority?: number;
      group?: string;
      render: () => { component: unknown };
    }) => void;
  };
};

let started = false;

/** The designer page does not enqueue addon stylesheets, so load ours next to the bundle. */
function ensureStyles(): void {
  if (document.querySelector('link[href*="/smart-configurator/dist/smart-configurator.bundle.css"]')) return;
  const entry = document.querySelector<HTMLScriptElement>('script[src*="/smart-configurator/dist/smart-configurator.bundle.js"]')?.src;
  if (!entry) return;
  const entryUrl = new URL(entry);
  const styleUrl = new URL('smart-configurator.bundle.css', entryUrl);
  styleUrl.search = entryUrl.search;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = styleUrl.href;
  document.head.appendChild(link);
}

function readConfig(payload: unknown): Record<string, unknown> | null {
  const fromPayload = (payload as { config?: Record<string, unknown> } | undefined)?.config;
  if (fromPayload && typeof fromPayload === 'object') return fromPayload;
  const element = document.getElementById('designer-config');
  if (!element) return null;
  try {
    return JSON.parse(element.textContent || '{}') as Record<string, unknown>;
  } catch {
    return null;
  }
}

function start(payload?: unknown): void {
  if (started) return;
  const runtime = (window as unknown as { Veyop?: VeyopDesignerRuntime }).Veyop;
  const config = readConfig(payload);
  if (!runtime?.designer?.registerLeftPanel || !config || !init(config)) return;
  started = true;
  ensureStyles();

  runtime.designer.registerLeftPanel({
    id: 'smart-configurator',
    group: 'product',
    label: t('Options'),
    description: t('Choose product options'),
    icon: 'M4 6h16M4 12h10M4 18h6M18 10l2 2-2 2',
    priority: 5,
    render: () => ({ component: DesignerOptionsPanel }),
  });

  runtime.hooks?.on?.('after.load.design', () => {
    resetBaseline();
    window.setTimeout(apply, 0);
  });
  // Commands and the design load shortly after mount; retry until the first apply lands.
  let tries = 0;
  const tick = () => {
    if (!apply() && ++tries < 40) window.setTimeout(tick, 250);
  };
  tick();
}

function boot(): void {
  const runtime = (window as unknown as { Veyop?: VeyopDesignerRuntime }).Veyop;
  runtime?.hooks?.whenReady?.('designer', start);
  runtime?.hooks?.on?.('veyop:designer:ready', start);
  start();
  const begin = Date.now();
  const poll = () => {
    if (started || Date.now() - begin > 8000) return;
    start();
    window.setTimeout(poll, 100);
  };
  poll();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
